import type { BlockState } from '@store/generated/appointments';

import type { EligibilityCheckResponse, PatientCostVisitContext, WorklistItem } from '../types';

export type PatientCost = NonNullable<EligibilityCheckResponse['patient_cost']>;
export const UNKNOWN_PATIENT_COST = 'Patient amount not determined';
const UNSAFE_COST_CLAUSE = /\$|\bcollect(?:ed)?\b|\bowe\b|\b(?:dollars?|cents?)\b|good to go/i;

export function formatUsdCents(cents: number): string {
  const exactCents = BigInt(cents);
  const dollars = exactCents / 100n;
  const remainder = exactCents % 100n;
  const dollarsLabel = dollars.toLocaleString('en-US');
  return remainder === 0n
    ? `$${dollarsLabel}`
    : `$${dollarsLabel}.${remainder.toString().padStart(2, '0')}`;
}

function nonnegativeCents(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/** A benefit or legacy obligation is never a visit amount. Context comes from the caller. */
export function confirmedPatientAmount(
  cost: PatientCost | null | undefined,
  context?: Partial<PatientCostVisitContext>,
): number | undefined {
  if (cost?.amount_status !== 'confirmed' || cost.policy_version !== 'fixed-copay-v2')
    return undefined;
  const visit = cost.visit_context;
  const source = cost.source;
  if (
    !Number.isSafeInteger(cost.amount_cents) ||
    cost.amount_cents < 0 ||
    typeof source?.id !== 'string' ||
    !source.id.trim() ||
    typeof source.revision !== 'string' ||
    !source.revision.trim() ||
    !Number.isFinite(Date.parse(source.confirmed_at)) ||
    !/(Z|[+-]\d{2}:\d{2})$/.test(source.confirmed_at) ||
    !['visit_amount_confirmation', 'final_claim_pr'].includes(source.kind) ||
    !context?.coverage_id ||
    context.coverage_id !== cost.coverage_id ||
    context.coverage_id !== visit?.coverage_id ||
    !context.service_date ||
    context.service_date !== visit.service_date ||
    (!context.encounter_id && !context.appointment_id) ||
    (context.encounter_id && context.encounter_id !== visit.encounter_id) ||
    (context.appointment_id && context.appointment_id !== visit.appointment_id)
  )
    return undefined;
  return cost.amount_cents;
}

/** Validate a server-projected benefit component, independently of the total. */
function projectedFixedCopay(cost: PatientCost | null | undefined): number | undefined {
  if (cost?.policy_version !== 'fixed-copay-v2' || cost.collection?.kind !== 'fixed_copay')
    return undefined;
  const amount = nonnegativeCents(cost.collection.amount_cents);
  return amount !== null && amount === nonnegativeCents(cost.copayment_cents) ? amount : undefined;
}

/** Payment defaults require the active coverage supplied by the caller. */
export function fixedCopayAmount(
  cost: PatientCost | null | undefined,
  coverageId: string | null | undefined,
): number | undefined {
  return coverageId && cost?.coverage_id === coverageId ? projectedFixedCopay(cost) : undefined;
}

export function collectiblePatientAmount(
  cost: PatientCost | null | undefined,
  context?: Partial<PatientCostVisitContext>,
): number | undefined {
  return confirmedPatientAmount(cost, context) ?? fixedCopayAmount(cost, context?.coverage_id);
}

/** A rejected confirmation must not be revalidated later with different caller context. */
function withoutConfirmedAmount(cost: PatientCost): PatientCost {
  if (cost.amount_status !== 'confirmed') return cost;
  return {
    ...cost,
    amount_status: 'undetermined',
    amount_cents: null,
    source: null,
    visit_context: null,
    reason: 'source_unavailable',
  };
}

function fixedCopayLabel(amount: number): string {
  return amount > 0 ? `Collect ${formatUsdCents(amount)} co-pay` : 'Co-pay $0';
}

function fixedCopaySummary(cost: PatientCost, amount: number, original?: string | null): string {
  const percent = benefitPercent({ coinsurance_percent: cost.coinsurance_percent });
  const routing = safeCostText(original, '')
    ?.split(/\.(?=\s|$)/)
    .map(clause => clause.trim())
    .filter(clause => clause && !/amount (?:is )?not determined|co-?pay|coinsurance/i.test(clause))
    .join('. ');
  return [
    routing ? `${routing}.` : null,
    `${fixedCopayLabel(amount)}.`,
    percent !== null ? `${percent}% coinsurance: dollar amount not determined.` : null,
    percent !== null ? 'Total visit amount not determined.' : null,
  ]
    .filter(Boolean)
    .join(' ');
}

function collectionReadiness(
  readiness: string | null | undefined,
  label: string | null | undefined,
): boolean {
  return (
    readiness === 'collect' ||
    readiness === 'verified' ||
    (readiness === 'info' && isBenefitLabel(label))
  );
}

export function benefitPercent(financials: Record<string, unknown>): number | null {
  for (const key of ['coinsurance_percent', 'coinsurance_cents', 'coinsurance']) {
    if (!(key in financials)) continue;
    const value = financials[key];
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100
      ? value
      : null;
  }
  return null;
}

export function benefitCents(value: unknown): number | null {
  return nonnegativeCents(value);
}

export function benefitLabel(
  cost: Pick<PatientCost, 'copayment_cents' | 'coinsurance_percent'> | null | undefined,
): string {
  const facts: string[] = [];
  const copayment = benefitCents(cost?.copayment_cents);
  if (copayment !== null) facts.push(`Co-pay ${formatUsdCents(copayment)}`);
  const percent = benefitPercent({ coinsurance_percent: cost?.coinsurance_percent });
  if (percent !== null) facts.push(`Coinsurance ${percent}%`);
  return facts.join(' · ') || UNKNOWN_PATIENT_COST;
}

/** Preserve independent routing/error text, but remove old calculated-dollar clauses. */
export function safeCostText(
  text: string | null | undefined,
  fallback = UNKNOWN_PATIENT_COST,
): string | null | undefined {
  if (!text || !UNSAFE_COST_CLAUSE.test(text)) return text;
  const retained = text
    .split(/[·;!]|\.(?=\s|$)|,\s+|\s*—\s*|\r?\n/)
    .filter(part => !UNSAFE_COST_CLAUSE.test(part))
    .map(part => part.trim())
    .filter(Boolean);
  return [...retained, fallback].filter(Boolean).join('. ');
}

function safeUnknownCostSummary(text: string | null | undefined): string {
  const sanitized = safeCostText(text);
  return !sanitized || sanitized === UNKNOWN_PATIENT_COST ? `${UNKNOWN_PATIENT_COST}.` : sanitized;
}

function paymentRecordedLabel(paidCents: number): string {
  return `Payment recorded (${formatUsdCents(paidCents)})`;
}

function paymentSummary(summary: string | null | undefined, paidCents: number): string {
  const routing = safeCostText(summary, '')?.trim();
  return [paymentRecordedLabel(paidCents), routing, UNKNOWN_PATIENT_COST]
    .filter(Boolean)
    .join('. ');
}

export function eligibilityVisitContext(
  data: EligibilityCheckResponse | undefined,
): Partial<PatientCostVisitContext> {
  return {
    coverage_id: data?.patient_insurance_coverage_id ?? undefined,
    encounter_id: data?.encounter_id ?? data?.pinned_encounter_id,
    appointment_id: data?.pinned_appointment_id,
    service_date: data?.pinned_appointment_at?.slice(0, 10),
  };
}

function currentBenefitLabel(data: EligibilityCheckResponse): string {
  return benefitLabel({
    copayment_cents: benefitCents(data.financials?.copayment_cents),
    coinsurance_percent: benefitPercent(data.financials ?? {}),
  });
}

function isBenefitLabel(label: string | null | undefined): boolean {
  return /^(co-?pay |coinsurance |patient amount not determined)/i.test(label ?? '');
}

/** Fail closed on cached pre-projection amounts without hiding coverage or billing warnings. */
export function safeEligibility<T extends EligibilityCheckResponse | undefined>(
  data: T,
  expectedContext?: Partial<PatientCostVisitContext>,
): T {
  if (!data || data.is_self_pay) return data;
  const paidCents = nonnegativeCents(data.billing?.total_paid_cents) ?? 0;
  const paid = paidCents > 0;
  const confirmed = confirmedPatientAmount(data.patient_cost, {
    coverage_id:
      expectedContext?.coverage_id ??
      data.patient_insurance_coverage_id ??
      data.patient_cost?.coverage_id ??
      undefined,
    encounter_id: expectedContext?.encounter_id,
    appointment_id: expectedContext?.appointment_id,
    service_date: expectedContext?.service_date,
  });
  const financials = { ...data.financials, member_obligation_cents: null };
  const copay = fixedCopayAmount(
    data.patient_cost,
    expectedContext?.coverage_id ?? data.patient_insurance_coverage_id,
  );
  const allowCollection =
    data.status === 'eligible' &&
    data.eligibility_check_state !== 'due' &&
    data.coverage_status !== 'inactive' &&
    !paid &&
    !data.visit_coverage_gap &&
    !data.visit_limit_warning &&
    collectionReadiness(data.readiness, data.readiness_label);
  if (confirmed !== undefined && allowCollection)
    return {
      ...data,
      financials,
      readiness_label: `Confirmed patient amount ${formatUsdCents(confirmed)}`,
      summary: 'Confirmed for this visit.',
    };
  if (copay !== undefined && allowCollection && data.patient_cost)
    return {
      ...data,
      financials,
      patient_cost: withoutConfirmedAmount(data.patient_cost),
      readiness: copay > 0 ? 'collect' : 'info',
      readiness_label: fixedCopayLabel(copay),
      summary: fixedCopaySummary(data.patient_cost, copay, data.summary),
    };
  const unsafeAmountState =
    !data.visit_coverage_gap &&
    !data.visit_limit_warning &&
    (data.readiness === 'collect' || (data.readiness === 'collected' && !paid));
  const verifiedCoverage =
    !data.visit_coverage_gap && !data.visit_limit_warning && data.readiness === 'verified';
  return {
    ...data,
    financials,
    patient_cost:
      data.patient_cost?.amount_status === 'confirmed'
        ? null
        : data.patient_cost && (!allowCollection || copay === undefined)
          ? { ...data.patient_cost, collection: null }
          : data.patient_cost,
    readiness: unsafeAmountState ? ('info' as const) : data.readiness,
    readiness_label:
      paid && data.readiness === 'collected'
        ? [paymentRecordedLabel(paidCents), safeCostText(data.readiness_label, '')]
            .filter(Boolean)
            .join(' · ')
        : unsafeAmountState
          ? safeCostText(data.readiness_label)
          : verifiedCoverage || isBenefitLabel(data.readiness_label)
            ? currentBenefitLabel(data)
            : safeCostText(data.readiness_label),
    summary: paid
      ? paymentSummary(data.summary, paidCents)
      : unsafeAmountState
        ? safeUnknownCostSummary(data.summary)
        : (safeCostText(data.summary) ?? ''),
  };
}

export function safeBlockState<T extends BlockState | undefined | null>(block: T): T {
  if (!block) return block;
  const eligibility = block.readiness_inputs?.eligibility;
  const paidCents = nonnegativeCents(block.readiness_inputs?.billing?.total_paid_cents) ?? 0;
  const paid = paidCents > 0;
  // Display uses the server's current envelope; money actions still require coverage identity.
  const copay = projectedFixedCopay(eligibility?.patient_cost);
  if (
    copay !== undefined &&
    eligibility?.patient_cost &&
    eligibility.status === 'eligible' &&
    !paid &&
    !block.coverage_gap &&
    !block.readiness_inputs?.visit_limit_warning &&
    collectionReadiness(block.readiness, block.readiness_label)
  )
    return {
      ...block,
      readiness: copay > 0 ? 'collect' : 'info',
      readiness_label: fixedCopayLabel(copay),
      readiness_inputs: {
        ...block.readiness_inputs,
        eligibility: {
          ...eligibility,
          member_obligation_cents: null,
          summary: fixedCopaySummary(eligibility.patient_cost, copay, eligibility.summary),
        },
      },
    };
  // A block has no independent coverage/date identity to validate a confirmed source.
  const unsafeAmountState =
    !block.coverage_gap &&
    !block.readiness_inputs?.visit_limit_warning &&
    (block.readiness === 'collect' || (block.readiness === 'collected' && !paid));
  const verifiedCoverage =
    !block.coverage_gap &&
    !block.readiness_inputs?.visit_limit_warning &&
    block.readiness === 'verified';
  return {
    ...block,
    readiness: unsafeAmountState ? 'info' : block.readiness,
    readiness_label:
      paid && block.readiness === 'collected'
        ? [paymentRecordedLabel(paidCents), safeCostText(block.readiness_label, '')]
            .filter(Boolean)
            .join(' · ')
        : unsafeAmountState
          ? safeCostText(block.readiness_label)
          : verifiedCoverage || isBenefitLabel(block.readiness_label)
            ? benefitLabel(eligibility?.patient_cost)
            : safeCostText(block.readiness_label),
    readiness_inputs: {
      ...block.readiness_inputs,
      eligibility: eligibility
        ? {
            ...eligibility,
            member_obligation_cents: null,
            summary: paid
              ? paymentSummary(eligibility.summary, paidCents)
              : unsafeAmountState
                ? safeUnknownCostSummary(eligibility.summary)
                : (safeCostText(eligibility.summary) ?? ''),
          }
        : undefined,
    },
  };
}

export function safeWorklistItem(item: WorklistItem): WorklistItem {
  if (item.is_self_pay)
    return {
      ...item,
      member_obligation_cents: null,
      readiness_label: safeCostText(item.readiness_label, 'Self-pay'),
      summary: safeCostText(item.summary, '') ?? '',
    };
  const context = {
    coverage_id: item.coverage_id ?? undefined,
    encounter_id: item.next_encounter_id,
    appointment_id: item.next_appointment_id,
    service_date: item.next_appointment_at?.slice(0, 10),
  };
  const confirmed = confirmedPatientAmount(item.patient_cost, context);
  const copay = fixedCopayAmount(item.patient_cost, context.coverage_id);
  const allowCollection = collectionReadiness(item.readiness, item.readiness_label);
  if (confirmed !== undefined && allowCollection)
    return {
      ...item,
      member_obligation_cents: null,
      readiness_label: `Confirmed patient amount ${formatUsdCents(confirmed)}`,
      summary: 'Confirmed for this visit.',
    };
  if (copay !== undefined && item.patient_cost && allowCollection)
    return {
      ...item,
      member_obligation_cents: null,
      patient_cost: withoutConfirmedAmount(item.patient_cost),
      readiness: copay > 0 ? 'collect' : 'info',
      readiness_label: fixedCopayLabel(copay),
      summary: fixedCopaySummary(item.patient_cost, copay, item.summary),
      action_verb: copay > 0 ? item.action_verb : 'View',
    };
  // WorklistItem has no billing or receipt evidence, so neither an amount to
  // collect nor an assertion that it was collected can be trusted here.
  const unsafeAmountState = item.readiness === 'collect' || item.readiness === 'collected';
  const verifiedCoverage = item.readiness === 'verified';
  return {
    ...item,
    member_obligation_cents: null,
    patient_cost:
      item.patient_cost?.amount_status === 'confirmed'
        ? null
        : item.patient_cost
          ? { ...item.patient_cost, collection: null }
          : item.patient_cost,
    readiness: unsafeAmountState ? 'info' : item.readiness,
    readiness_label:
      unsafeAmountState || verifiedCoverage || isBenefitLabel(item.readiness_label)
        ? benefitLabel(item.patient_cost)
        : safeCostText(item.readiness_label, UNKNOWN_PATIENT_COST),
    summary: safeCostText(item.summary) ?? '',
    action_verb: /collect|invoice/i.test(item.action_verb ?? '') ? null : item.action_verb,
  };
}

/** Bootstrap has no independent visit identity, so it cannot assert a confirmed total. */
export function safeHeaderCostLabel(
  data: Pick<EligibilityCheckResponse, 'readiness_label' | 'patient_cost'>,
): string | null | undefined {
  const copay = projectedFixedCopay(data.patient_cost);
  if (
    copay !== undefined &&
    (/^collect /i.test(data.readiness_label ?? '') || isBenefitLabel(data.readiness_label))
  )
    return fixedCopayLabel(copay);
  return isBenefitLabel(data.readiness_label)
    ? benefitLabel(data.patient_cost)
    : safeCostText(data.readiness_label);
}
